import { useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Check,
  CheckSquare,
  ChevronRight,
  Clock,
  FolderKanban,
  Package,
  Copy,
  FileText,
  Flag,
  Lightbulb,
  Link2,
  Loader2,
  MapPin,
  Maximize2,
  Pencil,
  Plus,
  Save,
  Scale,
  Trash2,
  User,
  Users,
  X,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { AssociationFields } from "@/components/shared/AssociationFields";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { formatDateTime } from "@/lib/utils";
import {
  useMeeting,
  useUpdateMeeting,
  useUpdateMeetingNotes,
  useUpdateMeetingAgenda,
  useAddAttendees,
  useRemoveAttendee,
  useUpdateAttendance,
  useDeleteMeeting,
  useMeetingSubEntity,
} from "../hooks/useMeetings";
import { QuickCreateModal, type QuickCreateType } from "../components/QuickCreateModal";
import { NewTaskDrawer } from "@/modules/tasks/components/NewTaskDrawer";
import { NewActionDrawer } from "@/modules/actions/components/NewActionDrawer";
import { NewNoteDrawer } from "@/modules/notes/components/NewNoteDrawer";
import { NewMilestoneDrawer } from "@/modules/milestones/components/NewMilestoneDrawer";
import { NewMeetingDrawer } from "../components/NewMeetingDrawer";
import { LinkExistingModal, type LinkExistingType } from "../components/LinkExistingModal";
import { LinkItemModal } from "../components/LinkItemModal";
import { TagBadge } from "@/components/ui/TagBadge";
import { Pin } from "lucide-react";
import { AddAttendeeModal } from "../components/AddAttendeeModal";
import { IdeasTab } from "@/modules/ideas/components/IdeasTab";
import { DecisionsTab } from "@/modules/decisions/components/DecisionsTab";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import { ExpensesTabContent } from "@/modules/expenses/components/ExpensesTabContent";
import { MilestoneStatusBadge } from "@/modules/milestones/components/MilestoneStatusBadge";
import { TaskStatusBadge } from "@/modules/tasks/components/TaskStatusBadge";
import { ActionStatusBadge } from "@/modules/actions/components/ActionStatusBadge";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { TaskStatus } from "@/modules/tasks/types";
import type { ActionStatus } from "@/modules/actions/types";
import type { MeetingStatus, MeetingType } from "../types";

const CARD_BORDER = "hsl(210,30%,85%)";

/* ───────── Badge helpers ───────── */

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    SCHEDULED: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)", label: "Scheduled" },
    IN_PROGRESS: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "In Progress" },
    COMPLETED: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)", label: "Completed" },
    CANCELLED: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)", label: "Cancelled" },
  };
  const s = styles[status] ?? styles.SCHEDULED;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

// Renders the right per-entity status badge for a row in the unified
// reverse-relationship table. The header badge above uses the
// Meeting-specific StatusBadge; this one is for linked Milestones / Tasks /
// Actions / Ideas / Decisions, which each have their own status vocabulary.
function LinkedRecordStatusBadge({
  type,
  status,
  ideaStatusLabels,
  decisionStatusLabels,
}: {
  type: "task" | "action" | "milestone" | "idea" | "decision" | string;
  status: string | null | undefined;
  ideaStatusLabels: Record<string, string>;
  decisionStatusLabels: Record<string, string>;
}) {
  if (!status) {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
        style={{ background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }}
      >
        —
      </span>
    );
  }
  switch (type) {
    case "milestone":
      return <MilestoneStatusBadge status={status} />;
    case "task":
      return <TaskStatusBadge status={status as TaskStatus} />;
    case "action":
      return <ActionStatusBadge status={status as ActionStatus} />;
    case "idea":
    case "decision": {
      const labels = type === "idea" ? ideaStatusLabels : decisionStatusLabels;
      const label =
        labels[status] ??
        status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return (
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ background: "hsl(210,55%,92%)", color: "hsl(210,100%,28%)" }}
        >
          {label}
        </span>
      );
    }
    default:
      return (
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }}
        >
          {status}
        </span>
      );
  }
}

function TypeBadge({ type }: { type: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    INTERNAL: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)", label: "Internal" },
    EXTERNAL: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "External" },
    CLIENT: { bg: "hsl(262,55%,92%)", fg: "hsl(262,83%,38%)", label: "Client" },
    PARTNER: { bg: "hsl(175,55%,92%)", fg: "hsl(175,60%,30%)", label: "Partner" },
    BOARD: { bg: "hsl(340,55%,92%)", fg: "hsl(340,72%,38%)", label: "Board" },
    STANDUP: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Stand-up" },
  };
  const s = styles[type] ?? styles.INTERNAL;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function CopyCodeBadge({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-mono text-xs text-muted-foreground transition-colors hover:bg-[hsl(210,16%,90%)]"
      style={{ background: "hsl(210,16%,93%)" }}
    >
      {code}
      {copied ? (
        <Check className="h-3 w-3 text-green-600" strokeWidth={2} />
      ) : (
        <Copy className="h-3 w-3" strokeWidth={1.5} />
      )}
    </button>
  );
}

/* ───────── Tab definitions ───────── */

type ItemTab =
  | "all"
  | "tasks"
  | "actions"
  | "notes"
  | "ideas"
  | "decisions"
  | "milestones"
  | "expenses"
  | "documents";

const ITEM_TABS: { key: ItemTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "tasks", label: "Tasks" },
  { key: "actions", label: "Actions" },
  { key: "notes", label: "Notes" },
  { key: "ideas", label: "Ideas" },
  { key: "decisions", label: "Decisions" },
  { key: "milestones", label: "Milestones" },
  { key: "expenses", label: "Expenses" },
  { key: "documents", label: "Documents" },
];

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */

export default function MeetingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data: meeting, isLoading, isError } = useMeeting(id!);
  const updateMeeting = useUpdateMeeting(id!);
  const updateNotes = useUpdateMeetingNotes(id!);
  const updateAgenda = useUpdateMeetingAgenda(id!);
  const addAttendees = useAddAttendees(id!);
  const removeAttendee = useRemoveAttendee(id!);
  const updateAttendance = useUpdateAttendance(id!);
  const deleteMeeting = useDeleteMeeting();

  const [editMode, setEditMode] = useState(searchParams.get("edit") === "true");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [itemTab, setItemTab] = useState<ItemTab>("all");

  const queryClient = useQueryClient();
  const [quickCreateType, setQuickCreateType] = useState<QuickCreateType | null>(null);
  const [showTaskDrawer, setShowTaskDrawer] = useState(false);
  const [showActionDrawer, setShowActionDrawer] = useState(false);
  const [showLinkTask, setShowLinkTask] = useState(false);
  const [showLinkAction, setShowLinkAction] = useState(false);
  const [showLinkNote, setShowLinkNote] = useState(false);
  const [linkType, setLinkType] = useState<LinkExistingType | null>(null);
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);
  const [milestoneDrawerOpen, setMilestoneDrawerOpen] = useState(false);
  const [showLinkMilestone, setShowLinkMilestone] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  // Notes tab local filters
  const [noteSearch, setNoteSearch] = useState("");
  const [noteTypeFilter, setNoteTypeFilter] = useState<string>("all");
  const [notePinnedOnly, setNotePinnedOnly] = useState(false);

  // Edit form state
  const [editTitle, setEditTitle] = useState("");
  const [editType, setEditType] = useState<MeetingType>("INTERNAL");
  const [editCategory, setEditCategory] = useState("");
  const [editStatus, setEditStatus] = useState<MeetingStatus>("SCHEDULED");
  const [editDate, setEditDate] = useState("");
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editMeetingUrl, setEditMeetingUrl] = useState("");
  const [editPhaseId, setEditPhaseId] = useState<string | null>(null);
  const [editStageId, setEditStageId] = useState<string | null>(null);
  const [editCompanyId, setEditCompanyId] = useState<string | null>(null);
  const [editProductId, setEditProductId] = useState<string | null>(null);
  const [editProjectId, setEditProjectId] = useState<string | null>(null);
  const [editOrganizerId, setEditOrganizerId] = useState<string>("");

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  // Idea / Decision statuses live in dropdown_configs (dropdown-driven, not
  // enums). The unified All tab needs the human label for those rows; we
  // fetch the maps once and pass them into the per-row badge component.
  const { data: ideaStatusItems = [] } = useCategoryItems("idea_status");
  const { data: decisionStatusItems = [] } = useCategoryItems("decision_status");
  const ideaStatusLabels = Object.fromEntries(
    ideaStatusItems.map((i) => [i.value, i.label]),
  );
  const decisionStatusLabels = Object.fromEntries(
    decisionStatusItems.map((i) => [i.value, i.label]),
  );

  // Rich-text content (edit mode locked by default)
  const [agendaHtml, setAgendaHtml] = useState("");
  const [notesHtml, setNotesHtml] = useState("");
  const [agendaSaved, setAgendaSaved] = useState("");
  const [notesSaved, setNotesSaved] = useState("");
  const [agendaEdit, setAgendaEdit] = useState(false);
  const [notesEdit, setNotesEdit] = useState(false);

  // Attendee add modal
  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removeAttendeeId, setRemoveAttendeeId] = useState<string | null>(null);

  // Expanded editor modal
  const [expandedEditor, setExpandedEditor] = useState<"agenda" | "notes" | null>(null);

  // Sub-entity data
  const tasks = useMeetingSubEntity<any>(id!, "tasks", { page: 1, limit: 50 });
  const actions = useMeetingSubEntity<any>(id!, "actions", { page: 1, limit: 50 });
  const ideas = useMeetingSubEntity<any>(id!, "ideas", { page: 1, limit: 50 });
  const decisions = useMeetingSubEntity<any>(id!, "decisions", { page: 1, limit: 50 });
  const milestones = useMeetingSubEntity<any>(id!, "milestones", { page: 1, limit: 50 });
  const notes = useMeetingSubEntity<any>(id!, "notes", { page: 1, limit: 100 });

  /* Populate state from fetched meeting */
  useEffect(() => {
    if (meeting) {
      setEditTitle(meeting.title);
      setEditType(meeting.type);
      setEditCategory(meeting.category ?? "");
      setEditStatus(meeting.status);
      const sch = new Date(meeting.scheduledAt);
      setEditDate(sch.toISOString().slice(0, 10));
      setEditStartTime(sch.toTimeString().slice(0, 5));
      if (meeting.endedAt) {
        setEditEndTime(new Date(meeting.endedAt).toTimeString().slice(0, 5));
      } else {
        setEditEndTime("");
      }
      setEditLocation(meeting.location ?? "");
      setEditMeetingUrl(meeting.meetingUrl ?? "");
      setEditPhaseId(meeting.phaseId ?? null);
      setEditStageId(meeting.stageId ?? null);
      setEditCompanyId(meeting.companyId ?? null);
      setEditProductId(meeting.productId ?? null);
      setEditProjectId(meeting.projectId ?? null);
      setEditOrganizerId(meeting.organizerId ?? "");
      setAgendaHtml(meeting.agenda ?? "");
      setAgendaSaved(meeting.agenda ?? "");
      setNotesHtml(meeting.notes ?? "");
      setNotesSaved(meeting.notes ?? "");
    }
  }, [meeting]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  /* ── Save handlers ── */

  async function handleSaveEdit() {
    const scheduledAt = new Date(`${editDate}T${editStartTime}`).toISOString();
    const endedAt = editEndTime ? new Date(`${editDate}T${editEndTime}`).toISOString() : null;
    await updateMeeting.mutateAsync({
      title: editTitle.trim(),
      type: editType,
      category: editCategory.trim() || null,
      status: editStatus,
      scheduledAt,
      endedAt,
      location: editLocation.trim() || null,
      meetingUrl: editMeetingUrl.trim() || null,
      phaseId: editPhaseId,
      stageId: editStageId,
      companyId: editCompanyId,
      productId: editProductId,
      projectId: editProjectId,
      organizerId: editOrganizerId || undefined,
    });
    setEditMode(false);
    showToast("Meeting updated");
  }

  const [agendaFlash, setAgendaFlash] = useState(false);
  const [notesFlash, setNotesFlash] = useState(false);

  const handleAgendaSave = useCallback(async () => {
    if (!id) return;
    await updateAgenda.mutateAsync(agendaHtml || null);
    setAgendaSaved(agendaHtml);
    setAgendaEdit(false);
    setAgendaFlash(true);
    setTimeout(() => setAgendaFlash(false), 1500);
  }, [agendaHtml, id, updateAgenda]);

  const handleAgendaCancel = useCallback(() => {
    setAgendaHtml(agendaSaved);
    setAgendaEdit(false);
  }, [agendaSaved]);

  const handleNotesSave = useCallback(async () => {
    if (!id) return;
    await updateNotes.mutateAsync(notesHtml || null);
    setNotesSaved(notesHtml);
    setNotesEdit(false);
    setNotesFlash(true);
    setTimeout(() => setNotesFlash(false), 1500);
  }, [notesHtml, id, updateNotes]);

  const handleNotesCancel = useCallback(() => {
    setNotesHtml(notesSaved);
    setNotesEdit(false);
  }, [notesSaved]);

  function handleDelete() {
    if (!meeting) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    await deleteMeeting.mutateAsync(id!);
    navigate("/meetings");
  }

  async function handleAddSelected(payload: {
    userIds?: string[];
    contactIds?: string[];
  }) {
    await addAttendees.mutateAsync(payload);
  }

  async function confirmRemoveAttendee() {
    if (!removeAttendeeId) return;
    await removeAttendee.mutateAsync(removeAttendeeId);
  }

  async function handleToggleAttendance(
    attendeeId: string,
    hasAttended: boolean,
  ) {
    await updateAttendance.mutateAsync({
      attendeeId,
      hasAttended: !hasAttended,
    });
  }

  /* ── Loading / error states ── */

  if (isLoading) {
    return (
      <AppLayout breadcrumb={[{ label: "PhazeX Hub", href: "/" }, { label: "Meetings", href: "/meetings" }, { label: "Loading..." }]}>
        <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading meeting...
        </div>
      </AppLayout>
    );
  }

  if (isError || !meeting) {
    return (
      <AppLayout breadcrumb={[{ label: "PhazeX Hub", href: "/" }, { label: "Meetings", href: "/meetings" }, { label: "Error" }]}>
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
          Meeting not found or failed to load.
        </div>
      </AppLayout>
    );
  }

  /* ── Derived values ── */

  const scheduledDate = new Date(meeting.scheduledAt);
  const endDate = meeting.endedAt ? new Date(meeting.endedAt) : null;
  const timeRange = `${scheduledDate.toTimeString().slice(0, 5)}${endDate ? ` – ${endDate.toTimeString().slice(0, 5)}` : ""}`;

  const allItems = [
    ...(actions.data?.data ?? []).map((a: any) => ({ ...a, _type: "action" })),
    ...(tasks.data?.data ?? []).map((t: any) => ({ ...t, _type: "task" })),
    ...(ideas.data?.data ?? []).map((i: any) => ({ ...i, _type: "idea" })),
    ...(decisions.data?.data ?? []).map((d: any) => ({ ...d, _type: "decision" })),
    ...(milestones.data?.data ?? []).map((m: any) => ({ ...m, _type: "milestone" })),
  ];

  function getTabData() {
    switch (itemTab) {
      case "tasks": return tasks.data?.data ?? [];
      case "actions": return actions.data?.data ?? [];
      case "notes": return notes.data?.data ?? [];
      case "ideas": return ideas.data?.data ?? [];
      case "decisions": return decisions.data?.data ?? [];
      case "milestones": return milestones.data?.data ?? [];
      default: return allItems;
    }
  }

  function getTabCount(tab: ItemTab): number {
    if (!meeting) return 0;
    switch (tab) {
      case "tasks": return meeting._count.tasks;
      case "actions": return meeting._count.actions;
      case "notes": return notes.data?.pagination?.total ?? (notes.data?.data?.length ?? 0);
      case "ideas": return meeting._count.ideas;
      case "decisions": return meeting._count.decisions;
      case "milestones": return meeting._count.milestones;
      case "expenses": return meeting._count.expenses ?? 0;
      case "documents": return meeting._count.documents ?? 0;
      case "all": return meeting._count.tasks + meeting._count.actions + meeting._count.ideas + meeting._count.decisions + meeting._count.milestones;
    }
  }

  const internalAttendees = meeting.attendees;
  const externalAttendees = meeting.externalAttendees ?? [];
  const totalAttendees = internalAttendees.length + externalAttendees.length;

  /* ═══════════════════════════════════════
     RENDER
     ═══════════════════════════════════════ */

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Daily Work" },
        { label: "Meetings", href: "/meetings" },
        { label: meeting.title },
      ]}
    >
      <div className="w-full space-y-4">
        {/* Toast */}
        {toast && (
          <div
            className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
            style={{ borderColor: "hsl(142,55%,80%)" }}
          >
            {toast}
          </div>
        )}

        {/* ──── Page header ──── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/meetings")}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <CalendarDays className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-xl font-semibold tracking-tight">Meeting Hub</h1>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setShowActionDrawer(true)} className="flex h-[28px] w-[28px] items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(210,50%,96%)]" title="Quick Add">
              <Plus className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button type="button" onClick={() => setHistoryOpen(true)} className="flex h-[28px] w-[28px] items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(210,50%,96%)]" title="Activity History">
              <Clock className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button type="button" className="flex h-[28px] w-[28px] items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(210,50%,96%)]" title="Export">
              <FileText className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button type="button" onClick={() => setDuplicateOpen(true)} className="flex h-[28px] w-[28px] items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(210,50%,96%)]" title="Duplicate this meeting">
              <Copy className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setEditMode(!editMode)}
              className={`flex h-[28px] w-[28px] items-center justify-center rounded-md transition-colors ${editMode ? "bg-[hsl(210,55%,92%)] text-[hsl(210,100%,28%)]" : "text-muted-foreground hover:bg-[hsl(210,50%,96%)]"}`}
              title="Edit"
            >
              <Pencil className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button type="button" onClick={handleDelete} className="flex h-[28px] w-[28px] items-center justify-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-600" title="Delete">
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* ════════════════════════════════════
           SECTION 1 — Meeting Header Card
           ════════════════════════════════════ */}
        <div className="rounded-xl border bg-white p-6" style={{ borderColor: CARD_BORDER }}>
          {editMode ? (
            <div className="space-y-4">
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="text-xl font-bold" />
              <div className="flex flex-wrap items-center gap-3">
                <CopyCodeBadge code={meeting.code} />
                <Select value={editStatus} onChange={(e) => setEditStatus(e.target.value as MeetingStatus)} className="w-36">
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </Select>
                <Select value={editType} onChange={(e) => setEditType(e.target.value as MeetingType)} className="w-32">
                  <option value="INTERNAL">Internal</option>
                  <option value="EXTERNAL">External</option>
                  <option value="CLIENT">Client</option>
                  <option value="PARTNER">Partner</option>
                  <option value="BOARD">Board</option>
                  <option value="STANDUP">Stand-up</option>
                </Select>
                <Input value={editCategory} onChange={(e) => setEditCategory(e.target.value)} placeholder="Category" className="w-36" />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} className="w-40" />
                <Input type="time" value={editStartTime} onChange={(e) => setEditStartTime(e.target.value)} className="w-32" />
                <span className="text-muted-foreground">–</span>
                <Input type="time" value={editEndTime} onChange={(e) => setEditEndTime(e.target.value)} className="w-32" />
                <Input value={editLocation} onChange={(e) => setEditLocation(e.target.value)} placeholder="Location" className="w-40" />
                <Input value={editMeetingUrl} onChange={(e) => setEditMeetingUrl(e.target.value)} placeholder="Meeting URL" className="w-48" />
              </div>
              <AssociationFields
                companyId={editCompanyId}
                productId={editProductId}
                projectId={editProjectId}
                onChange={(field, value) => {
                  if (field === "companyId") setEditCompanyId(value);
                  else if (field === "productId") setEditProductId(value);
                  else if (field === "projectId") {
                    setEditProjectId(value);
                    setEditPhaseId(null);
                    setEditStageId(null);
                  }
                }}
              />
              {editProjectId && (
                <PhaseStagePicker
                  projectId={editProjectId}
                  phaseId={editPhaseId}
                  stageId={editStageId}
                  onPhaseChange={setEditPhaseId}
                  onStageChange={setEditStageId}
                />
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Organizer</label>
                <select
                  value={editOrganizerId}
                  onChange={(e) => setEditOrganizerId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">Select organizer...</option>
                  {allUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveEdit} disabled={updateMeeting.isPending}>
                  {updateMeeting.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Save className="mr-1 h-4 w-4" strokeWidth={1.5} />}
                  Save
                </Button>
                <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-foreground">{meeting.title}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <CopyCodeBadge code={meeting.code} />
                <StatusBadge status={meeting.status} />
                <TypeBadge type={meeting.type} />
                {meeting.category && (
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium" style={{ background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }}>
                    {meeting.category}
                  </span>
                )}
                {meeting.project && (
                  <button type="button" onClick={() => navigate(`/projects/${meeting.project!.id}`)} className="text-xs text-muted-foreground hover:text-primary">
                    {meeting.project.name}
                  </button>
                )}
                {!meeting.project && meeting.company && (
                  <span className="text-xs text-muted-foreground">{meeting.company.name}</span>
                )}
                {meeting.phaseName && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: "hsl(210,55%,92%)", color: "hsl(210,100%,28%)" }}>
                    {meeting.phaseName}
                  </span>
                )}
                {meeting.stageName && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: "hsl(175,55%,92%)", color: "hsl(175,60%,30%)" }}>
                    {meeting.stageName}
                  </span>
                )}
              </div>
              {(meeting.company || meeting.product || meeting.project) && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[13px]">
                  {meeting.company && (
                    <button
                      type="button"
                      onClick={() => navigate(`/companies/${meeting.company!.id}`)}
                      className="inline-flex items-center gap-1 text-foreground transition-colors hover:text-primary"
                    >
                      <Building2 className="h-3 w-3" strokeWidth={1.5} />
                      {meeting.company.name}
                    </button>
                  )}
                  {meeting.company && meeting.product && (
                    <ChevronRight className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                  )}
                  {meeting.product && (
                    <button
                      type="button"
                      onClick={() => navigate(`/products/${meeting.product!.id}`)}
                      className="inline-flex items-center gap-1 text-foreground transition-colors hover:text-primary"
                    >
                      <Package className="h-3 w-3" strokeWidth={1.5} />
                      {meeting.product.name}
                    </button>
                  )}
                  {(meeting.company || meeting.product) && meeting.project && (
                    <ChevronRight className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                  )}
                  {meeting.project && (
                    <button
                      type="button"
                      onClick={() => navigate(`/projects/${meeting.project!.id}`)}
                      className="inline-flex items-center gap-1 text-foreground transition-colors hover:text-primary"
                    >
                      <FolderKanban className="h-3 w-3" strokeWidth={1.5} />
                      {meeting.project.name}
                    </button>
                  )}
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-5 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" strokeWidth={1.5} />{formatDateTime(meeting.scheduledAt)}</span>
                <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" strokeWidth={1.5} />{timeRange}</span>
                {meeting.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />{meeting.location}</span>}
                <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" strokeWidth={1.5} />{meeting.organizerName}</span>
                <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" strokeWidth={1.5} />{meeting._count.attendees}</span>
                <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" strokeWidth={1.5} />{meeting._count.actions}</span>
                <span className="flex items-center gap-1.5"><CheckSquare className="h-3.5 w-3.5" strokeWidth={1.5} />{meeting._count.tasks}</span>
              </div>
            </>
          )}
        </div>

        {/* ════════════════════════════════════
           SECTION 2 — Agenda (60%) + Attendees (40%)
           ════════════════════════════════════ */}
        <NewMeetingDrawer
          isOpen={duplicateOpen}
          onClose={() => setDuplicateOpen(false)}
          duplicateFrom={meeting}
          onCreated={(code) => showToast(`Meeting created: ${code}`)}
        />

        <div className="grid gap-4" style={{ gridTemplateColumns: "60fr 40fr" }}>
          {/* ── Agenda Card ── */}
          <div className="rounded-xl border bg-white" style={{ borderColor: CARD_BORDER }}>
            <div className="flex items-center justify-between border-b px-5 py-3" style={{ borderColor: CARD_BORDER }}>
              <div className="flex items-center gap-2">
                <h3 className="text-[13px] font-semibold text-foreground">Agenda</h3>
                {agendaFlash && (
                  <span className="text-[11px] font-medium" style={{ color: "hsl(142,72%,32%)" }}>
                    Saved
                  </span>
                )}
              </div>
              <div className="flex items-center gap-0.5">
                {!agendaEdit ? (
                  <button
                    type="button"
                    onClick={() => setAgendaEdit(true)}
                    className="section-icon-btn"
                    title="Edit"
                  >
                    <Pencil className="h-[14px] w-[14px]" strokeWidth={1.5} />
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleAgendaSave}
                      className="section-icon-btn section-icon-save"
                      title="Save"
                    >
                      <Check className="h-[14px] w-[14px]" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={handleAgendaCancel}
                      className="section-icon-btn section-icon-cancel"
                      title="Cancel"
                    >
                      <X className="h-[14px] w-[14px]" strokeWidth={1.5} />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setExpandedEditor("agenda")}
                  className="section-icon-btn"
                  title="Expand"
                >
                  <Maximize2 className="h-[14px] w-[14px]" strokeWidth={1.5} />
                </button>
              </div>
            </div>
            <div className={agendaEdit ? "p-0" : "px-5 py-4"}>
              <RichTextEditor
                content={agendaHtml}
                onChange={(html) => setAgendaHtml(html)}
                placeholder="Add meeting agenda items..."
                minHeight={200}
                toolbarVariant="minimal"
                readOnly={!agendaEdit}
              />
            </div>
          </div>

          {/* ── Attendees Card ── */}
          <div className="rounded-xl border bg-white" style={{ borderColor: CARD_BORDER }}>
            <div className="flex items-center justify-between border-b px-5 py-3" style={{ borderColor: CARD_BORDER }}>
              <div className="flex items-center gap-2">
                <h3 className="text-[13px] font-semibold text-foreground">Attendees</h3>
                <span
                  className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
                  style={{ background: "hsl(210,55%,92%)", color: "hsl(210,100%,28%)" }}
                >
                  {totalAttendees}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowAddAttendee(true)}
              >
                Add
              </Button>
            </div>

            <div className="px-4 py-3">
              {/* (Add modal is rendered separately at bottom) */}

              {/* INTERNAL */}
              <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Internal ({internalAttendees.length})
              </div>
              {internalAttendees.length === 0 ? (
                <div className="py-4 text-center text-xs text-muted-foreground">No internal attendees</div>
              ) : (
                <div className="space-y-0">
                  {internalAttendees.map((att) => (
                    <div
                      key={att.id}
                      className="group flex items-center gap-2.5 rounded-md px-2 py-2 transition-colors hover:bg-[hsl(210,50%,98%)]"
                    >
                      <div
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                        style={{
                          background: att.hasAttended ? "hsl(142,55%,92%)" : "hsl(210,16%,93%)",
                          color: att.hasAttended ? "hsl(142,72%,28%)" : "hsl(215,16%,40%)",
                        }}
                      >
                        {att.userName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium text-foreground">{att.userName}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleToggleAttendance(att.id, att.hasAttended)}
                        className="cursor-pointer"
                      >
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors"
                          style={att.hasAttended
                            ? { background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }
                            : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,47%)" }
                          }
                        >
                          {att.hasAttended ? "Attended" : "Not Attended"}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setRemoveAttendeeId(att.id)}
                        className="hidden h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-500 group-hover:flex"
                      >
                        <X className="h-3 w-3" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Divider */}
              <div className="my-3 border-t" style={{ borderColor: "hsl(210,30%,90%)" }} />

              {/* EXTERNAL */}
              <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                External ({externalAttendees.length})
              </div>
              {externalAttendees.length === 0 ? (
                <div className="py-4 text-center text-xs text-muted-foreground">No external attendees</div>
              ) : (
                <div className="space-y-0">
                  {externalAttendees.map((att) => (
                    <div
                      key={att.id}
                      className="group flex items-center gap-2.5 rounded-md px-2 py-2 transition-colors hover:bg-[hsl(210,50%,98%)]"
                    >
                      <div
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                        style={{ background: "hsl(38,92%,92%)", color: "hsl(38,92%,30%)" }}
                      >
                        {att.contactName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium text-foreground">{att.contactName}</div>
                        {att.companyName && (
                          <div className="truncate text-[11px] text-muted-foreground">{att.companyName}</div>
                        )}
                      </div>
                      <button type="button" onClick={() => handleToggleAttendance(att.id, att.hasAttended)} className="cursor-pointer">
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                          style={att.hasAttended
                            ? { background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }
                            : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,47%)" }
                          }
                        >
                          {att.hasAttended ? "Attended" : "Not Attended"}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setRemoveAttendeeId(att.id)}
                        className="hidden h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-500 group-hover:flex"
                      >
                        <X className="h-3 w-3" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════
           SECTION 3 — Notes / Minutes (full width)
           ════════════════════════════════════ */}
        <div className="rounded-xl border bg-white" style={{ borderColor: CARD_BORDER }}>
          <div className="flex items-center justify-between border-b px-5 py-3" style={{ borderColor: CARD_BORDER }}>
            <div className="flex items-center gap-2">
              <h3 className="text-[13px] font-semibold text-foreground">Notes / Minutes</h3>
              {notesFlash && (
                <span className="text-[11px] font-medium" style={{ color: "hsl(142,72%,32%)" }}>
                  Saved
                </span>
              )}
            </div>
            <div className="flex items-center gap-0.5">
              {!notesEdit ? (
                <button
                  type="button"
                  onClick={() => setNotesEdit(true)}
                  className="section-icon-btn"
                  title="Edit"
                >
                  <Pencil className="h-[14px] w-[14px]" strokeWidth={1.5} />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleNotesSave}
                    className="section-icon-btn section-icon-save"
                    title="Save"
                  >
                    <Check className="h-[14px] w-[14px]" strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={handleNotesCancel}
                    className="section-icon-btn section-icon-cancel"
                    title="Cancel"
                  >
                    <X className="h-[14px] w-[14px]" strokeWidth={1.5} />
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setExpandedEditor("notes")}
                className="section-icon-btn"
                title="Expand"
              >
                <Maximize2 className="h-[14px] w-[14px]" strokeWidth={1.5} />
              </button>
            </div>
          </div>
          <div className={notesEdit ? "p-0" : "px-5 py-4"}>
            <RichTextEditor
              content={notesHtml}
              onChange={(html) => setNotesHtml(html)}
              placeholder="Capture meeting notes and minutes..."
              minHeight={250}
              toolbarVariant="full"
              readOnly={!notesEdit}
            />
          </div>

          {/* Quick Insert bar — only when Notes in edit mode */}
          {notesEdit && (
            <div className="flex items-center gap-2.5 border-t px-5 py-3" style={{ borderColor: CARD_BORDER }}>
              <span className="text-xs text-muted-foreground">Quick insert:</span>
              {([
                { type: "action" as QuickCreateType, label: "Action", icon: Zap, color: "hsl(38,92%,45%)" },
                { type: "task" as QuickCreateType, label: "Task", icon: CheckSquare, color: "hsl(210,100%,45%)" },
                { type: "idea" as QuickCreateType, label: "Idea", icon: Lightbulb, color: "hsl(262,60%,55%)" },
                { type: "decision" as QuickCreateType, label: "Decision", icon: Scale, color: "hsl(175,60%,38%)" },
                { type: "milestone" as QuickCreateType, label: "Milestone", icon: Flag, color: "hsl(142,55%,38%)" },
              ]).map(({ type, label, icon: Icon, color }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    if (type === "task") setShowTaskDrawer(true);
                    else if (type === "action") setShowActionDrawer(true);
                    else if (type === "milestone") setMilestoneDrawerOpen(true);
                    else setQuickCreateType(type);
                  }}
                  className="inline-flex h-7 items-center gap-[5px] rounded-full border bg-white px-3 text-xs font-medium text-foreground transition-all hover:bg-[hsl(210,16%,95%)]"
                  style={{ borderColor: CARD_BORDER }}
                >
                  <Icon className="h-[13px] w-[13px]" strokeWidth={1.5} style={{ color }} />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ════════════════════════════════════
           SECTION 4 — Meeting Items tabs
           ════════════════════════════════════ */}
        <div className="space-y-4">
          <div className="flex items-center gap-1 border-b" style={{ borderColor: CARD_BORDER }}>
            {ITEM_TABS.map((tab) => {
              const count = getTabCount(tab.key);
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setItemTab(tab.key)}
                  className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                    itemTab === tab.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab.label}
                  {count > 0 && (
                    <span
                      className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
                      style={{
                        background: itemTab === tab.key ? "hsl(210,55%,92%)" : "hsl(210,16%,93%)",
                        color: itemTab === tab.key ? "hsl(210,100%,28%)" : "hsl(215,16%,40%)",
                      }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {itemTab !== "all" &&
            itemTab !== "ideas" &&
            itemTab !== "decisions" &&
            itemTab !== "notes" &&
            itemTab !== "expenses" &&
            itemTab !== "documents" && (
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                onClick={() => {
                  if (itemTab === "tasks") setShowTaskDrawer(true);
                  else if (itemTab === "actions") setShowActionDrawer(true);
                  else if (itemTab === "milestones") setMilestoneDrawerOpen(true);
                  else setQuickCreateType(itemTab as QuickCreateType);
                }}
              >
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                Create New
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (itemTab === "tasks") setShowLinkTask(true);
                  else if (itemTab === "actions") setShowLinkAction(true);
                  else if (itemTab === "milestones") setShowLinkMilestone(true);
                  else setLinkType(itemTab as QuickCreateType);
                }}
              >
                <Link2 className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                Link Existing
              </Button>
            </div>
          )}

          {itemTab === "ideas" && (
            <IdeasTab
              filter={{ meetingId: id! }}
              prefill={{
                meetingId: id!,
                meetingTitle: meeting.title,
                companyId: meeting.companyId,
                productId: meeting.productId,
                projectId: meeting.projectId,
                locked: { meetingId: true },
              }}
            />
          )}

          {itemTab === "decisions" && (
            <DecisionsTab
              filter={{ meetingId: id! }}
              prefill={{
                meetingId: id!,
                meetingTitle: meeting.title,
                companyId: meeting.companyId,
                productId: meeting.productId,
                projectId: meeting.projectId,
              }}
            />
          )}

          {itemTab === "notes" && (() => {
            const allNotes: any[] = notes.data?.data ?? [];
            const noteTypes = Array.from(new Set(allNotes.map((n) => n.type).filter(Boolean)));
            const filtered = allNotes.filter((n) => {
              if (notePinnedOnly && !n.isPinned) return false;
              if (noteTypeFilter !== "all" && n.type !== noteTypeFilter) return false;
              const q = noteSearch.trim().toLowerCase();
              if (q && !(n.title?.toLowerCase().includes(q) || n.content?.toLowerCase().includes(q))) return false;
              return true;
            });

            return (
              <div className="space-y-4">
                {/* Summary card */}
                <div
                  className="flex items-center gap-3 rounded-xl border bg-white px-4 py-3"
                  style={{ borderColor: CARD_BORDER }}
                >
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-lg"
                    style={{ background: "hsl(210,55%,94%)", color: "hsl(210,100%,35%)" }}
                  >
                    <FileText className="h-[18px] w-[18px]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Total Notes
                    </div>
                    <div className="text-lg font-semibold text-foreground">
                      {allNotes.length}
                    </div>
                  </div>
                </div>

                {/* Filter row */}
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    value={noteSearch}
                    onChange={(e) => setNoteSearch(e.target.value)}
                    placeholder="Search notes..."
                    className="h-8 w-64 text-sm"
                  />
                  <Select
                    value={noteTypeFilter}
                    onChange={(e) => setNoteTypeFilter(e.target.value)}
                    className="h-8 w-40 text-sm"
                  >
                    <option value="all">All Types</option>
                    {noteTypes.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </Select>
                  <button
                    type="button"
                    onClick={() => setNotePinnedOnly((v) => !v)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition-colors"
                    style={{
                      borderColor: notePinnedOnly ? "hsl(40,95%,60%)" : CARD_BORDER,
                      background: notePinnedOnly ? "hsl(40,95%,95%)" : "white",
                      color: notePinnedOnly ? "hsl(40,95%,30%)" : "hsl(215,16%,47%)",
                    }}
                  >
                    <Pin
                      className="h-[13px] w-[13px]"
                      strokeWidth={1.5}
                      style={{ fill: notePinnedOnly ? "hsl(40,95%,55%)" : "none" }}
                    />
                    Pinned Only
                  </button>
                </div>

                {/* Grid or empty state */}
                {filtered.length === 0 ? (
                  <div
                    className="flex flex-col items-center justify-center rounded-xl border bg-white py-16"
                    style={{ borderColor: CARD_BORDER }}
                  >
                    <FileText className="h-8 w-8 text-muted-foreground/40" strokeWidth={1.5} />
                    <p className="mt-2 text-sm text-muted-foreground">
                      No notes yet for this meeting
                    </p>
                    <Button
                      size="sm"
                      className="mt-4"
                      onClick={() => setNoteDrawerOpen(true)}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                      Add Note
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {filtered.map((n: any) => (
                      <div
                        key={n.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/notes/${n.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") navigate(`/notes/${n.id}`);
                        }}
                        className="cursor-pointer rounded-xl border bg-white p-[14px] transition-colors hover:bg-[hsl(210,50%,98%)]"
                        style={{ borderColor: CARD_BORDER }}
                      >
                        {/* Header row */}
                        <div className="mb-1.5 flex items-center justify-between">
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium capitalize"
                            style={{
                              background: "hsl(210,55%,92%)",
                              color: "hsl(210,100%,28%)",
                            }}
                          >
                            {n.type ?? "general"}
                          </span>
                          <Pin
                            className="h-[13px] w-[13px]"
                            strokeWidth={1.5}
                            style={{
                              color: n.isPinned ? "hsl(40,95%,45%)" : "hsl(215,16%,60%)",
                              fill: n.isPinned ? "hsl(40,95%,55%)" : "none",
                            }}
                          />
                        </div>

                        {/* Title */}
                        <div
                          className="mb-1 truncate text-foreground"
                          style={{ fontSize: 13, fontWeight: 600 }}
                        >
                          {n.title}
                        </div>

                        {/* Content preview */}
                        {n.content && (
                          <div
                            className="mb-2 text-muted-foreground"
                            style={{
                              fontSize: 12,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {n.content.slice(0, 80)}
                            {n.content.length > 80 ? "…" : ""}
                          </div>
                        )}

                        {/* Tags */}
                        {Array.isArray(n.tags) && n.tags.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-1">
                            {n.tags.slice(0, 2).map((t: string) => (
                              <TagBadge key={t} value={t} label={t} size="sm" />
                            ))}
                            {n.tags.length > 2 && (
                              <span className="inline-flex items-center rounded-full bg-[hsl(210,16%,93%)] px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                                +{n.tags.length - 2}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Footer */}
                        <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                          <span className="font-mono">{n.code ?? ""}</span>
                          <span>
                            {n.createdByName ? `${n.createdByName} · ` : ""}
                            {n.createdAt ? new Date(n.createdAt).toLocaleDateString() : ""}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Footer buttons */}
                <div className="flex items-center justify-end gap-2">
                  <Button size="sm" onClick={() => setNoteDrawerOpen(true)}>
                    <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                    New Note
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowLinkNote(true)}>
                    <Link2 className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                    Link Note
                  </Button>
                </div>
              </div>
            );
          })()}

          {itemTab === "expenses" && (
            <ExpensesTabContent meetingId={id!} />
          )}

          {itemTab === "documents" && (
            <FileUploadZone
              entityType="meeting"
              entityId={meeting.id}
              entityLabel={meeting.code}
              meetingId={meeting.id}
              showExisting
            />
          )}

          {itemTab !== "ideas" &&
            itemTab !== "decisions" &&
            itemTab !== "notes" &&
            itemTab !== "expenses" &&
            itemTab !== "documents" && (
          <div className="overflow-hidden rounded-xl border bg-card" style={{ borderColor: CARD_BORDER }}>
            {getTabData().length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">No items in this tab yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <tr>
                    {itemTab === "all" && <th className="px-6 py-3">Type</th>}
                    <th className="px-6 py-3">Title</th>
                    <th className="px-6 py-3">Status</th>
                    {(itemTab === "tasks" || itemTab === "actions" || itemTab === "all") && (
                      <>
                        <th className="px-6 py-3">Priority</th>
                        <th className="px-6 py-3">Due Date</th>
                        <th className="px-6 py-3">Assignee</th>
                      </>
                    )}
                    {itemTab === "milestones" && (
                      <>
                        <th className="px-6 py-3">Progress</th>
                        <th className="px-6 py-3">Due Date</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {getTabData().map((item: any) => (
                    <tr key={item.id} className="transition-colors hover:bg-[hsl(210,50%,98%)]">
                      {itemTab === "all" && (
                        <td className="px-6 py-3">
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium capitalize" style={{ background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }}>
                            {item._type}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-3 font-medium text-foreground">{item.title}</td>
                      <td className="px-6 py-3">
                        <LinkedRecordStatusBadge
                          type={
                            item._type ??
                            (itemTab === "all" ? "" : itemTab.replace(/s$/, ""))
                          }
                          status={item.status}
                          ideaStatusLabels={ideaStatusLabels}
                          decisionStatusLabels={decisionStatusLabels}
                        />
                      </td>
                      {(itemTab === "tasks" || itemTab === "actions" || itemTab === "all") && (
                        <>
                          <td className="px-6 py-3 text-muted-foreground">{item.priority ?? "—"}</td>
                          <td className="px-6 py-3 text-muted-foreground">{item.dueDate ? new Date(item.dueDate).toLocaleDateString() : "—"}</td>
                          <td className="px-6 py-3 text-muted-foreground">{item.assignee ?? "—"}</td>
                        </>
                      )}
                      {itemTab === "milestones" && (
                        <>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-16 rounded-full bg-[hsl(210,16%,93%)]">
                                <div className="h-full rounded-full bg-primary" style={{ width: `${item.progress ?? 0}%` }} />
                              </div>
                              <span className="text-xs text-muted-foreground">{item.progress ?? 0}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-3 text-muted-foreground">{item.dueDate ? new Date(item.dueDate).toLocaleDateString() : "—"}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          )}
        </div>

        {/* Attachments are now rendered inside the Documents tab above */}

        {/* ══════════ Expanded editor modal ══════════ */}
        {expandedEditor && (
          <>
            <div
              className="fixed inset-0 z-[9998] bg-black/30"
              onClick={() => setExpandedEditor(null)}
            />
            <div className="fixed inset-8 z-[9999] flex flex-col rounded-xl border bg-white shadow-2xl" style={{ borderColor: CARD_BORDER }}>
              <div className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: CARD_BORDER }}>
                <h2 className="text-base font-semibold text-foreground">
                  {expandedEditor === "agenda" ? "Agenda" : "Notes / Minutes"}
                </h2>
                <button
                  type="button"
                  onClick={() => setExpandedEditor(null)}
                  className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex-1 overflow-auto">
                <RichTextEditor
                  content={expandedEditor === "agenda" ? agendaHtml : notesHtml}
                  onChange={(html) => {
                    if (expandedEditor === "agenda") setAgendaHtml(html);
                    else setNotesHtml(html);
                  }}
                  placeholder={expandedEditor === "agenda" ? "Add meeting agenda items..." : "Capture meeting notes and minutes..."}
                  minHeight={500}
                  toolbarVariant={expandedEditor === "agenda" ? "minimal" : "full"}
                  readOnly={
                    expandedEditor === "agenda" ? !agendaEdit : !notesEdit
                  }
                />
              </div>
            </div>
          </>
        )}

        {/* ══════════ Add Attendee modal ══════════ */}
        <AddAttendeeModal
          isOpen={showAddAttendee}
          onClose={() => setShowAddAttendee(false)}
          existingUserIds={internalAttendees
            .map((a) => a.userId)
            .filter((x): x is string => !!x)}
          existingContactIds={externalAttendees.map((a) => a.contactId)}
          onAdd={handleAddSelected}
        />

        <style>{`
          .section-icon-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 26px;
            height: 26px;
            border-radius: 4px;
            border: none;
            background: transparent;
            color: hsl(215, 16%, 47%);
            cursor: pointer;
            transition: background-color 120ms, color 120ms;
          }
          .section-icon-btn:hover {
            background: hsl(210, 16%, 93%);
            color: hsl(215, 25%, 15%);
          }
          .section-icon-save:hover {
            background: hsl(173, 50%, 88%);
            color: hsl(173, 100%, 28%);
          }
          .section-icon-cancel:hover {
            background: hsl(0, 50%, 93%);
            color: hsl(0, 84%, 50%);
          }
        `}</style>

        {/* ══════════ Quick create modal ══════════ */}
        {quickCreateType && quickCreateType !== "task" && quickCreateType !== "action" && (
          <QuickCreateModal
            isOpen
            onClose={() => setQuickCreateType(null)}
            type={quickCreateType}
            meetingId={id!}
            companyId={meeting.companyId}
            productId={meeting.productId}
            projectId={meeting.projectId}
            onCreated={(code) => {
              showToast(`${quickCreateType} created: ${code}`);
              setQuickCreateType(null);
            }}
          />
        )}

        {/* ══════════ New Task drawer (for Task quick-insert / Add Task) ══════════ */}
        <NewTaskDrawer
          isOpen={showTaskDrawer}
          onClose={() => setShowTaskDrawer(false)}
          prefill={{
            meetingId: id!,
            meetingTitle: meeting.title,
            companyId: meeting.companyId,
            productId: meeting.productId,
            projectId: meeting.projectId,
            locked: { meetingId: true },
          }}
          onCreated={(code) => {
            showToast(`Task created: ${code}`);
            setShowTaskDrawer(false);
            queryClient.invalidateQueries({ queryKey: ["meeting", id] });
            queryClient.invalidateQueries({ queryKey: ["meetings"] });
          }}
        />

        <NewActionDrawer
          isOpen={showActionDrawer}
          onClose={() => setShowActionDrawer(false)}
          prefill={{
            meetingId: id!,
            meetingTitle: meeting.title,
            companyId: meeting.companyId,
            productId: meeting.productId,
            projectId: meeting.projectId,
            locked: {
              meetingId: true,
              projectId: !!meeting.projectId,
              companyId: !!meeting.companyId,
              productId: !!meeting.productId,
            },
          }}
          onCreated={(code) => {
            showToast(`Action created: ${code}`);
            setShowActionDrawer(false);
            queryClient.invalidateQueries({ queryKey: ["meeting", id] });
            queryClient.invalidateQueries({ queryKey: ["meetings"] });
          }}
        />

        {/* ══════════ New Note drawer ══════════ */}
        <NewNoteDrawer
          isOpen={noteDrawerOpen}
          onClose={() => setNoteDrawerOpen(false)}
          prefill={{
            meetingId: id!,
            meetingTitle: meeting.title,
            companyId: meeting.companyId,
            productId: meeting.productId,
            projectId: meeting.projectId,
            locked: {
              meetingId: true,
              companyId: !!meeting.companyId,
              productId: !!meeting.productId,
              projectId: !!meeting.projectId,
            },
          }}
          onCreated={(code) => {
            showToast(`Note created: ${code}`);
            setNoteDrawerOpen(false);
          }}
        />

        {/* ══════════ Link existing modal ══════════ */}
        {linkType && (
          <LinkExistingModal
            isOpen
            onClose={() => setLinkType(null)}
            type={linkType}
            meetingId={id!}
            onLinked={() => {
              showToast(`${linkType} linked to meeting`);
              setLinkType(null);
            }}
          />
        )}

        {/* ══════════ Link Task / Link Action (debounced auto-search) ══════════ */}
        <LinkItemModal
          isOpen={showLinkTask}
          onClose={() => setShowLinkTask(false)}
          type="task"
          meetingId={meeting.id}
          linkedIds={(tasks.data?.data ?? []).map((t: any) => t.id)}
          onLinked={() => {
            showToast("Task linked to meeting");
            queryClient.invalidateQueries({ queryKey: ["meeting", meeting.id] });
            queryClient.invalidateQueries({ queryKey: ["meetings"] });
          }}
        />
        <LinkItemModal
          isOpen={showLinkAction}
          onClose={() => setShowLinkAction(false)}
          type="action"
          meetingId={meeting.id}
          linkedIds={(actions.data?.data ?? []).map((a: any) => a.id)}
          onLinked={() => {
            showToast("Action linked to meeting");
            queryClient.invalidateQueries({ queryKey: ["meeting", meeting.id] });
            queryClient.invalidateQueries({ queryKey: ["meetings"] });
          }}
        />
        <LinkItemModal
          isOpen={showLinkNote}
          onClose={() => setShowLinkNote(false)}
          type="note"
          meetingId={meeting.id}
          linkedIds={(notes.data?.data ?? []).map((n: any) => n.id)}
          onLinked={() => {
            showToast("Note linked to meeting");
            queryClient.invalidateQueries({ queryKey: ["meeting", meeting.id] });
            queryClient.invalidateQueries({ queryKey: ["meetings"] });
          }}
        />
        <LinkItemModal
          isOpen={showLinkMilestone}
          onClose={() => setShowLinkMilestone(false)}
          type="milestone"
          meetingId={meeting.id}
          linkedIds={(milestones.data?.data ?? []).map((m: any) => m.id)}
          onLinked={() => {
            showToast("Milestone linked to meeting");
            queryClient.invalidateQueries({ queryKey: ["meeting", meeting.id] });
            queryClient.invalidateQueries({ queryKey: ["meetings"] });
          }}
        />
        <NewMilestoneDrawer
          isOpen={milestoneDrawerOpen}
          onClose={() => setMilestoneDrawerOpen(false)}
          prefill={{
            meetingId: meeting.id,
            meetingTitle: meeting.title,
            companyId: meeting.companyId,
            productId: meeting.productId,
            projectId: meeting.projectId,
            locked: { meetingId: true },
          }}
          onCreated={(code) => showToast(`Milestone created: ${code}`)}
        />

        {/* ══════════ Activity History ══════════ */}
        <ActivityHistoryDrawer
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="meeting"
          entityId={id!}
        />

        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete meeting?"
          description={meeting ? `Are you sure you want to delete '${meeting.title}'? It will be removed from your Meetings list.` : ""}
          onConfirm={confirmDelete}
        />

        <ConfirmDialog
          open={removeAttendeeId !== null}
          onOpenChange={(open) => {
            if (!open) setRemoveAttendeeId(null);
          }}
          title="Remove attendee?"
          description={(() => {
            if (!removeAttendeeId) return "";
            const internal = internalAttendees.find((a) => a.id === removeAttendeeId);
            const external = externalAttendees.find((a) => a.id === removeAttendeeId);
            const name = internal?.userName ?? external?.contactName ?? "this attendee";
            return `Are you sure you want to remove '${name}' from this meeting?`;
          })()}
          confirmLabel="Remove"
          onConfirm={confirmRemoveAttendee}
        />
      </div>
    </AppLayout>
  );
}
